import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  customersApi,
  accountSettlementsApi,
  type Customer,
  type CustomerAccount,
} from "./api";

export const customerKeys = {
  all: ["customers"] as const,
  lists: () => [...customerKeys.all, "list"] as const,
  list: (params: Parameters<typeof customersApi.list>[0]) => [...customerKeys.lists(), params] as const,
  accounts: () => [...customerKeys.all, "account"] as const,
  account: (id: number) => [...customerKeys.accounts(), id] as const,
};

export function useCustomers(
  params: Parameters<typeof customersApi.list>[0] = {},
  enabled = true
) {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () => customersApi.list(params),
    enabled,
  });
}

export function useCustomerAccount(id: number | null, enabled = true) {
  return useQuery<CustomerAccount>({
    queryKey: customerKeys.account(id ?? -1),
    queryFn: () => customersApi.account(id!),
    enabled: enabled && id != null,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof customersApi.create>[0]) => customersApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

export function usePatchCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof customersApi.patch>[1] }) =>
      customersApi.patch(id, body),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: customerKeys.lists() });
      qc.invalidateQueries({ queryKey: customerKeys.accounts() });
      qc.setQueryData(customerKeys.account(c.id), (prev: CustomerAccount | undefined) =>
        prev ? { ...prev, customer: c } : prev
      );
    },
  });
}

export function useCreateAccountSettlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, body }: { customerId: number; body: Parameters<typeof accountSettlementsApi.create>[1] }) => accountSettlementsApi.create(customerId, body),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: customerKeys.account(vars.customerId) });
      qc.invalidateQueries({ queryKey: customerKeys.lists() });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

// Convenience selector: build the customer's full display name.
export function fullName(c: Pick<Customer, "firstName" | "lastName">): string {
  return [c.firstName, c.lastName ?? ""].filter(Boolean).join(" ").trim();
}
